import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../lib/firebase';
import { collection, query, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';
import { onForegroundMessage } from '../lib/notifications';

// Création du contexte
const NotificationContext = createContext(null);

// Hook personnalisé pour utiliser le contexte
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications doit être utilisé dans un NotificationProvider');
  }
  return context;
};

// Helper pour formater la date
const formatNotificationDate = (timestamp) => {
  if (!timestamp) return 'À l\'instant';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString('fr-FR');
};

// Provider du contexte
export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();

  const [notifications, setNotifications] = useState([]);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  // Listener pour les notifications en temps réel
  useEffect(() => {
    if (!user || !activeCompanyId) {
      setNotifications([]);
      return;
    }

    const notificationsQuery = query(
      collection(db, 'companies', activeCompanyId, 'notifications'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const notifs = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const readByUser = data.readBy && data.readBy[user.uid];
        return {
          id: docSnap.id,
          ...data,
          read: readByUser || false,
          date: formatNotificationDate(data.createdAt)
        };
      });
      setNotifications(notifs);
    }, (error) => {
      console.error('Error loading notifications:', error);
    });

    return () => unsubscribe();
  }, [user, activeCompanyId]);

  // Gérer les messages de notifications au premier plan
  useEffect(() => {
    if (!user || !activeCompanyId) return;

    const unsubscribe = onForegroundMessage((payload) => {
      console.log('Foreground notification received:', payload);

      if (Notification.permission === 'granted') {
        const notification = new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: '/manifest-icon-192.maskable.png',
          badge: '/manifest-icon-192.maskable.png',
          tag: payload.data?.docId || 'ovrio-notif',
          requireInteraction: false
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user, activeCompanyId]);

  // Afficher le prompt de notification après un délai
  useEffect(() => {
    if (!user || !activeCompanyId) return;

    const hasSeenPrompt = localStorage.getItem('notification-prompt-seen');
    if (!hasSeenPrompt) {
      const timer = setTimeout(() => {
        setShowNotificationPrompt(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [user, activeCompanyId]);

  // Marquer une notification comme lue
  const markAsRead = useCallback(async (id) => {
    if (!user || !activeCompanyId) return;
    try {
      const notifRef = doc(db, 'companies', activeCompanyId, 'notifications', id);
      await updateDoc(notifRef, {
        read: true,
        [`readBy.${user.uid}`]: true
      });
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  }, [user, activeCompanyId]);

  // Marquer toutes les notifications comme lues
  const markAllAsRead = useCallback(async () => {
    if (!user || !activeCompanyId) return;
    try {
      const unreadNotifs = notifications.filter(n => !n.read);
      for (const notif of unreadNotifs) {
        const notifRef = doc(db, 'companies', activeCompanyId, 'notifications', notif.id);
        await updateDoc(notifRef, {
          read: true,
          [`readBy.${user.uid}`]: true
        });
      }
    } catch (e) {
      console.error('Error marking all as read:', e);
    }
  }, [user, activeCompanyId, notifications]);

  // Fermer le prompt de notification
  const dismissNotificationPrompt = useCallback(() => {
    setShowNotificationPrompt(false);
    localStorage.setItem('notification-prompt-seen', 'true');
  }, []);

  // Nombre de notifications non lues
  const unreadCount = useMemo(() =>
    notifications.filter(n => !n.read).length,
    [notifications]
  );

  const value = {
    notifications,
    unreadCount,
    showNotificationPrompt,
    markAsRead,
    markAllAsRead,
    dismissNotificationPrompt
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
